import React, { useRef } from "react";
import PrimaryButton from "../../common/Button/PrimaryButton";
import RouteNames from "../../../Routes/RouteNames";
import { useRouter } from "next/router";
import Image from "next/image";
import CircleSpinner from "../../common/Spinner/CircleSpinner";

type Tab = {
  title: string;
  key: string;
  desktopHeight?: number;
  desktopWidth?: number;
  mobileHeight?: number;
  mobileWidth?: number;
  mobileBlurHash?: string;
  desktopBlurHash?: string;
};

const TemplateHighlight = () => {
  const router = useRouter();
  const tabs: Tab[] = [
    {
      title: "New Year",
      key: "new-year",
      desktopHeight: 727,
      desktopWidth: 1417,
      mobileHeight: 2958,
      mobileWidth: 670,
      mobileBlurHash: "L9Mj,bR?yYoa?[-:g1xZ0PRWM_o|",
      desktopBlurHash: "LFMQx8Iw%L%epA~pIB-qoNIVM|oM",
    },
    {
      title: "Valentine's Day",
      key: "valentines-day",
      desktopHeight: 719,
      desktopWidth: 1418,
      mobileHeight: 2794,
      mobileWidth: 670,
      mobileBlurHash: "L7PsCq4T_N?^0000IV%h*dH@M{kV",
      desktopBlurHash: "L9P~=;00Dg9Fv4DiHrrE?]%##T;|",
    },
    {
      title: "Christmas",
      key: "christmas",
      desktopHeight: 727,
      desktopWidth: 1417,
      mobileHeight: 2875,
      mobileWidth: 670,
      mobileBlurHash: "L1NS]I_OBvAU000000%O005IzV8^",
      desktopBlurHash: "L8N9%w004Tmm9H9r0z_N%2DOD5%1",
    },
    {
      title: "Birthday",
      key: "birthday",
      desktopHeight: 702,
      desktopWidth: 1419,
      mobileHeight: 2316,
      mobileWidth: 671,
      mobileBlurHash: "LFNAbk+ppdIx_4%~jErqTbtKIVXT",
      desktopBlurHash: "LBN0@]~7.lTb-GEC.i=o9*9d9Z^{",
    },
    {
      title: "Halloween",
      key: "halloween",
      desktopHeight: 347,
      desktopWidth: 1417,
      mobileHeight: 1550,
      mobileWidth: 671,
      mobileBlurHash: "LBLN0+?KMxR40~0dJB%g9E4TVsi_",
      desktopBlurHash: "L3KwIJ0300220000?wl-tp00d;-W",
    },
  ];

  const [selectedTab, setSelectedTab] = React.useState(tabs[0]);
  const [isLoading, setIsLoading] = React.useState(false);

  function handleTabClick(tab: Tab) {
    setIsLoading(true);
    setSelectedTab(tab);
  }

  const divRef = useRef<HTMLDivElement>(null);
  let pos = { top: 0, left: 0, x: 0, y: 0 };

  const mouseDownHandler = function (e: any) {
    if (divRef.current) {
      pos = {
        left: divRef.current.scrollLeft,
        top: divRef.current.scrollTop,
        // Get the current mouse position
        x: e.clientX,
        y: e.clientY,
      };

      divRef.current.addEventListener("mousemove", mouseMoveHandler);
      divRef.current.addEventListener("mouseup", mouseUpHandler);
    }
  };

  const mouseMoveHandler = function (e: any) {
    if (divRef.current) {
      // How far the mouse has been moved
      const dx = e.clientX - pos.x;
      const dy = e.clientY - pos.y;

      // Scroll the element
      divRef.current.scrollTop = pos.top - dy;
      divRef.current.scrollLeft = pos.left - dx;
    }
  };

  const mouseUpHandler = function () {
    if (divRef.current) {
      divRef.current.removeEventListener("mousemove", mouseMoveHandler);
      divRef.current.removeEventListener("mouseup", mouseUpHandler);
    }
  };

  return (
    <div className="py-[96px] px-8 flex flex-col items-center justify-center bg-[#FFFAEE] text-black my-[40px]">
      <div className="hidden md:block">
        <div className="flex flex-col items-center justify-center">
          <h2 className="font-bold text-4xl">
            Create beautiful photo collages instantly
          </h2>
          <h1 className="font-bold text-4xl -mt-2">with our templates.</h1>
        </div>
      </div>
      <div className="md:hidden">
        <h2 className="font-bold text-3xl">
          Create beautiful photo collages instantly with our templates.
        </h2>
      </div>
      <p className="max-w-4xl pt-6 md:text-center">
        Transform your cherished memories effortlessly into captivating visual
        stories! Dive into our collection of professionally-crafted photo
        collage templates, meticulously designed to inspire awe. Whether
        it&apos;s commemorating milestones or cherishing everyday moments, our
        vast collage template library offers endless possibilities. With just a
        few clicks, infuse your personal flair and creativity, turning each
        collage into a masterpiece uniquely yours.
      </p>
      <div
        className="flex gap-4 pt-6 overflow-x-auto w-full sm:justify-center no-scrollbar"
        onMouseDown={mouseDownHandler}
        ref={divRef}
      >
        {tabs.map((tab, index) => (
          <button
            key={index}
            onClick={function () {
              return handleTabClick(tab);
            }}
            className={`${
              tab.key == selectedTab.key
                ? "border border-solid border-yellow bg-[#FFF3E9]"
                : ""
            } text-black px-4 text-xs py-1 rounded-full cursor-pointer whitespace-nowrap`}
          >
            {tab.title}
          </button>
        ))}
      </div>

      <div className="pt-8 sm:block hidden md:px-8 lg:px-24">
        {isLoading && (
          <CircleSpinner
            className={`w-[1417px] h-[340px] flex items-center justify-center`}
          />
        )}
        <Image
          className={`${isLoading ? "hidden" : "block"}`}
          src={`/static/images/${
            selectedTab.key
          }-templates.webp?time=${new Date().getTime()}`}
          alt={`${selectedTab} template`}
          width={selectedTab?.desktopWidth}
          height={selectedTab?.desktopHeight}
          onLoad={(e) => {
            console.log("loaded");
            return setIsLoading(false);
          }}
        ></Image>
      </div>
      <div className="pt-8 sm:hidden w-full">
        {isLoading && (
          <CircleSpinner
            className={`w-full h-[300px] flex items-center justify-center`}
          />
        )}
        <Image
          className={`${isLoading ? "hidden" : "block"}`}
          src={`/static/images/${
            selectedTab.key
          }-mobile-templates.webp?time=${new Date().getTime()}`}
          alt={`${selectedTab} template`}
          width={selectedTab?.mobileWidth}
          height={selectedTab?.mobileHeight}
          onLoad={(e) => setIsLoading(false)}
        ></Image>
      </div>

      <PrimaryButton
        className="rounded-md mt-8 md:mt-4"
        text="Explore all templates"
        onClick={() => {
          void router.push(`${RouteNames.collageEditor}?tab=templates`);
        }}
      />
    </div>
  );
};

export default TemplateHighlight;
